import useDeepMemo from '@/utils/useDeepMemo';
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface UseAutoResetPageIndexParams {
  filter: string | undefined;
  sortingKey: string;
  groupingKey: string;
  externalInteractionSignal?: unknown;
  hasLazyLoading?: boolean;
}

/**
 * Computes `autoResetPageIndex` for a TanStack table in a way that respects
 * deep-link row highlights.
 *
 * Behavior:
 * - When the table uses server-driven (lazy) pagination, autoReset is always
 *   suppressed. With `manualPagination: true`, every server fetch produces a
 *   new `data` array reference; letting TanStack auto-reset would bounce the
 *   user back to page 0 on every page click. Parents of lazy tables are
 *   responsible for resetting pagination explicitly (e.g. on filter change).
 * - If the URL contains `highlightField` + `highlightValue` on mount, autoReset
 *   is suppressed so upstream data-ref changes (including background refetches
 *   and polling) don't bounce the user off the targeted page.
 * - As soon as the user interacts with filter/sort/grouping or the optional
 *   external interaction signal changes, autoReset flips back to true
 *   SYNCHRONOUSLY in the same render so TanStack sees the new option together
 *   with the new data ref.
 * - Once the interaction is detected, the highlight params are stripped from
 *   the URL (cosmetic; not in the critical path).
 */
export const useAutoResetPageIndex = ({
  filter,
  sortingKey,
  groupingKey,
  externalInteractionSignal,
  hasLazyLoading = false,
}: UseAutoResetPageIndexParams): boolean => {
  const { search } = useLocation();
  const navigate = useNavigate();

  const stableExternalSignal = useDeepMemo(externalInteractionSignal);

  const hadHighlightOnMountRef = useRef<boolean>(false);
  const initialFilterRef = useRef<string | undefined>(filter);
  const initialSortingKeyRef = useRef<string>('');
  const initialGroupingKeyRef = useRef<string>('');
  const initialExternalSignalRef = useRef<unknown>(stableExternalSignal);
  const didCaptureInitialRef = useRef<boolean>(false);

  if (!didCaptureInitialRef.current) {
    const initialParams = new URLSearchParams(search);
    hadHighlightOnMountRef.current = !!(initialParams.get('highlightField') && initialParams.get('highlightValue'));
    initialFilterRef.current = filter;
    initialSortingKeyRef.current = sortingKey;
    initialGroupingKeyRef.current = groupingKey;
    initialExternalSignalRef.current = stableExternalSignal;
    didCaptureInitialRef.current = true;
  }

  const userHasInteracted =
    filter !== initialFilterRef.current ||
    sortingKey !== initialSortingKeyRef.current ||
    groupingKey !== initialGroupingKeyRef.current ||
    stableExternalSignal !== initialExternalSignalRef.current;

  const autoResetPageIndex = !hasLazyLoading && (!hadHighlightOnMountRef.current || userHasInteracted);

  useEffect(() => {
    if (!hadHighlightOnMountRef.current || !userHasInteracted) return;
    const params = new URLSearchParams(search);
    if (!params.has('highlightField') && !params.has('highlightValue')) return;
    params.delete('highlightField');
    params.delete('highlightValue');
    const nextSearch = params.toString();
    navigate({ search: nextSearch ? `?${nextSearch}` : '' }, { replace: true });
  }, [userHasInteracted, search, navigate]);

  return autoResetPageIndex;
};
